/**
 * ListNotasDebito.js — Lista de Notas de Débito CxC
 * Espejo de ListNotasCredito.js contra /api/finance/notaDebitoCxC/.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { formatCurrency } from "../../../../shared/utils/formatCurrency";
import { downloadExcel } from "../../shared/excelService";
import ConfirmModal from "../../shared/ConfirmModal";
import styles from "../styles/invoice.module.css";

const PAGE_SIZE = 20;

const MOTIVO_LABELS = {
  INTERESES: "notasDebito.motivoIntereses",
  RECARGO: "notasDebito.motivoRecargo",
  CORRECCION: "notasDebito.motivoCorreccion",
  CARGO: "notasDebito.motivoCargo",
  OTRO: "notasDebito.motivoOtro",
};

/** "2025-01-15" → "15/01/2025" */
function fmtDate(iso) {
  if (!iso || iso.length < 10) return iso ?? "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ListNotasDebito() {
  const { t } = useTranslation();
  const _rol = localStorage.getItem("rol") || "USUARIO";
  const _isSuper = localStorage.getItem("is_superuser") === "true";
  const isContador = _isSuper || _rol !== "RESIDENTE";

  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  const [exportando, setExportando] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    apiFetch(`/api/finance/notaDebitoCxC/?page=${page}&page_size=${PAGE_SIZE}`)
      .then((r) => r.json())
      .then((d) => {
        setItems(d.results ?? []);
        setCount(d.count ?? 0);
        setNext(d.next);
        setPrevious(d.previous);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    // refreshKey fuerza la recarga aunque no se use en el cuerpo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, refreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function showMsg(ok, text) {
    setActionMsg({ ok, text });
    setTimeout(() => setActionMsg(null), 4000);
  }

  async function confirmarEliminar() {
    const nd = confirmando;
    if (!nd) return;
    setDeletingId(nd.id);
    try {
      const res = await apiFetch(`/api/finance/notaDebitoCxC/${nd.id}/`, {
        method: "DELETE",
      });
      if (res.status !== 204) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || t("notasDebito.errorEliminar"));
      }
      showMsg(true, t("notasDebito.eliminada"));
      setRefreshKey((k) => k + 1);
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setDeletingId(null);
      setConfirmando(null);
    }
  }

  async function handleExportar() {
    setExportando(true);
    try {
      await downloadExcel("/api/finance/notaDebitoCxC/exportar-excel/", "notas_debito_cxc.xlsx");
    } catch (e) {
      showMsg(false, e.message);
    } finally {
      setExportando(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const colSpan = isContador ? 6 : 5;

  return (
    <>
      {/* Breadcrumb */}
      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <Link to="/finanzas/cxc">
          <span className={styles.inactive}> {t("facturas.cuentasPorCobrar")} /</span>
        </Link>
        <span className={styles.active}> {t("notasDebito.titulo")}</span>
      </span>

      {/* Header */}
      <div
        className="d-flex justify-content-between align-items-center flex-wrap gap-2"
        style={{ marginBottom: "6px" }}
      >
        <div>
          <h2 className="mt-2 me-2 p-0 title">{t("notasDebito.gestion")}</h2>
          <p className="mb-3" style={{ color: "var(--text-muted)" }}>
            {t("notasDebito.administra")}
          </p>
        </div>
        <button
          type="button"
          onClick={handleExportar}
          disabled={exportando}
          className="btn"
          style={{
            background: "rgba(74,222,128,0.1)",
            border: "1px solid rgba(74,222,128,0.3)",
            color: "#4ade80",
            padding: "7px 14px",
            fontSize: "0.875rem",
            fontWeight: 400,
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {exportando ? (
            <i className="fa-solid fa-circle-notch fa-spin" />
          ) : (
            <i className="fa-solid fa-file-export" />
          )}
          {t("common.exportar")}
        </button>
      </div>

      {/* Tabla */}
      <div className={styles["table-container"]}>
        <div className={`${styles["table-body"]} ${styles["table-responsive"]}`}>
          <table>
            <thead>
              <tr>
                <th>{t("notasDebito.colNumero")}</th>
                <th>{t("notasDebito.colFactura")}</th>
                <th>{t("notasDebito.colMotivo")}</th>
                <th>{t("common.fecha")}</th>
                <th className={styles.monto}>{t("notasDebito.colMonto")}</th>
                {isContador && <th className="text-center">{t("common.acciones")}</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: 32, color: "var(--text-secondary)" }}
                  >
                    <i
                      className="fa-solid fa-circle-notch fa-spin"
                      style={{ marginRight: 8, color: "#f97316" }}
                    />
                    {t("notasDebito.cargando")}
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: 32, color: "var(--text-muted)" }}
                  >
                    <i className="fa-solid fa-inbox" style={{ marginRight: 8, fontSize: "1.2rem" }} />
                    {t("notasDebito.sinNotas")}
                  </td>
                </tr>
              ) : (
                items.map((nd) => (
                  <tr key={nd.id}>
                    <td style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                      {nd.numero_nota}
                    </td>
                    <td>
                      {nd.factura?.numero_factura ?? "-"}
                      {nd.factura?.filial?.idFilial && (
                        <span
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            color: "var(--text-secondary)",
                            marginTop: 1,
                          }}
                        >
                          {nd.factura.filial.idFilial}
                        </span>
                      )}
                    </td>
                    <td>{t(MOTIVO_LABELS[nd.motivo] ?? "notasDebito.motivoOtro")}</td>
                    <td>{fmtDate(nd.fecha)}</td>
                    <td>
                      <div style={{ textAlign: "right", fontFamily: "Inter", fontWeight: 300 }}>
                        {nd.moneda === "USD" ? (
                          <span style={{ color: "#60a5fa" }}>
                            ${Number(nd.monto).toFixed(2)}
                          </span>
                        ) : (
                          <>₡{formatCurrency(nd.monto)}</>
                        )}
                      </div>
                    </td>
                    {isContador && (
                      <td className="text-center">
                        <button
                          type="button"
                          onClick={() => setConfirmando(nd)}
                          disabled={deletingId === nd.id}
                          title={t("common.eliminar")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 30,
                            height: 30,
                            padding: 0,
                            background: "rgba(248,113,113,0.1)",
                            border: "1px solid rgba(248,113,113,0.3)",
                            borderRadius: 6,
                            color: "#f87171",
                            fontSize: "0.85rem",
                            cursor: deletingId === nd.id ? "not-allowed" : "pointer",
                          }}
                        >
                          {deletingId === nd.id ? (
                            <i className="fa-solid fa-circle-notch fa-spin" />
                          ) : (
                            <i className="fa-solid fa-trash" />
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer paginación */}
        <div className={styles["table-footer"]}>
          <p>
            {loading ? (
              <span style={{ color: "#f97316", fontSize: "0.83rem" }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight: 5 }} />
                {t("common.cargando")}
              </span>
            ) : (
              <>
                <span>{count}</span> {t("common.resultados")} · {t("common.pagina")}{" "}
                <span>{page}</span> {t("common.de")} <span>{totalPages}</span>
              </>
            )}
          </p>
          <div className={`${styles["pagination-buttons"]} d-flex gap-2`}>
            <button
              disabled={!previous || loading}
              onClick={() => setPage((p) => p - 1)}
              className={`btn btn-sm ${styles["btn-prev"]} d-flex gap-2 align-items-center`}
            >
              {t("common.anterior")}
            </button>
            <button
              disabled={!next || loading}
              onClick={() => setPage((p) => p + 1)}
              className={`btn btn-sm ${styles["btn-next"]} d-flex gap-2 align-items-center`}
            >
              {t("common.siguiente")}
            </button>
          </div>
        </div>
      </div>

      {confirmando && (
        <ConfirmModal
          title={t("notasDebito.confirmarEliminarTitulo")}
          message={t("notasDebito.confirmarEliminar", { numero: confirmando.numero_nota })}
          loading={deletingId === confirmando.id}
          onConfirm={confirmarEliminar}
          onCancel={() => setConfirmando(null)}
        />
      )}

      {/* Toast flotante */}
      {actionMsg && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 2000,
            background: actionMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(251,146,60,0.07)",
            border: `1px solid ${actionMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(251,146,60,0.3)"}`,
            borderLeft: `4px solid ${actionMsg.ok ? "#4ade80" : "#fb923c"}`,
            borderRadius: 8,
            padding: "12px 18px",
            display: "flex",
            alignItems: "center",
            gap: 10,
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            maxWidth: 380,
          }}
        >
          <i
            className={`fa-solid ${actionMsg.ok ? "fa-circle-check" : "fa-triangle-exclamation"}`}
            style={{ color: actionMsg.ok ? "#4ade80" : "#fb923c", fontSize: "1.1rem" }}
          />
          <span
            style={{
              color: actionMsg.ok ? "#4ade80" : "#fb923c",
              fontWeight: 600,
              fontSize: "0.88rem",
            }}
          >
            {actionMsg.text}
          </span>
        </div>
      )}
    </>
  );
}
